'use client';

import { useEffect, useState, useMemo } from 'react';
import {
  DocumentExceptionErrorStatusEnum,
  DocumentExceptionErrorTypeEnum,
} from '../../common/enums.ts';
import queryHooks from '../../common/queryHooks/index.ts';
import { useDebounceValue } from '../../common/useDebounce.ts';
import { useNavigate } from 'react-router-dom';
import M from 'materialize-css';
import styled from '@emotion/styled';

export default function DocumentExceptions() {
  const navigate = useNavigate();

  const errorStatusOptions = useMemo(
    () => [
      { label: 'All', value: null },
      { label: 'Open', value: DocumentExceptionErrorStatusEnum.Open },
      { label: 'Closed', value: DocumentExceptionErrorStatusEnum.Closed },
    ],
    []
  );

  const errorTypeOptions = useMemo(
    () => [
      { label: 'All', value: null },
      {
        label: 'MalformedEpcisXmlError',
        value: DocumentExceptionErrorTypeEnum.MALFORMED_EPCIS_XML_ERROR,
      },
      {
        label: 'StandardBusinessDocumentHeaderError',
        value:
          DocumentExceptionErrorTypeEnum.STANDARD_BUSINESS_DOCUMENT_HEADER_ERROR,
      },
      {
        label: 'HeaderHeaderVersionError',
        value: DocumentExceptionErrorTypeEnum.HEADER_HEADER_VERSION_ERROR,
      },
      {
        label: 'HeaderReceiverSglnError',
        value: DocumentExceptionErrorTypeEnum.HEADER_RECEIVER_SGLN_ERROR,
      },
      {
        label: 'HeaderSenderError',
        value: DocumentExceptionErrorTypeEnum.HEADER_SENDER_ERROR,
      },
      {
        label: 'HeaderDocumentIdentificationError',
        value:
          DocumentExceptionErrorTypeEnum.HEADER_DOCUMENT_IDENTIFICATION_ERROR,
      },
      {
        label: 'HeaderDocumentIdentificationTypeError',
        value:
          DocumentExceptionErrorTypeEnum.HEADER_DOCUMENT_IDENTIFICATION_TYPE_ERROR,
      },
      {
        label: 'HeaderDocumentIdentificationCreationDateAndTimeError',
        value:
          DocumentExceptionErrorTypeEnum.HEADER_DOCUMENT_IDENTIFICATION_CREATION_DATE_AND_TIME_ERROR,
      },
      {
        label: 'HeaderDocumentIdentificationInstanceIdentifierError',
        value:
          DocumentExceptionErrorTypeEnum.HEADER_DOCUMENT_IDENTIFICATION_INSTANCE_IDENTIFIER_ERROR,
      },
      {
        label: 'LocationVocabularyError',
        value: DocumentExceptionErrorTypeEnum.LOCATION_VOCABULARY_ERROR,
      },
      {
        label: 'MedVocabularyError',
        value: DocumentExceptionErrorTypeEnum.MED_VOCABULARY_ERROR,
      },
      {
        label: 'EventMedsError',
        value: DocumentExceptionErrorTypeEnum.EVENT_MEDS_ERROR,
      },
      {
        label: 'EventListError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_ERROR,
      },
      {
        label: 'EventListEventTimeError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_EVENT_TIME_ERROR,
      },
      {
        label: 'EventListEventTimeZoneOffsetError',
        value:
          DocumentExceptionErrorTypeEnum.EVENT_LIST_EVENT_TIME_ZONE_OFFSET_ERROR,
      },
      {
        label: 'EventListActionError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_ACTION_ERROR,
      },
      {
        label: 'EventListBizStepError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_BIZ_STEP_ERROR,
      },
      {
        label: 'EventListDispositionError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_DISPOSITION_ERROR,
      },
      {
        label: 'EventListEpcListError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_EPC_LIST_ERROR,
      },
      {
        label: 'EventListReadPointError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_READ_POINT_ERROR,
      },
      {
        label: 'EventListBizTransactionListError',
        value:
          DocumentExceptionErrorTypeEnum.EVENT_LIST_BIZ_TRANSACTION_LIST_ERROR,
      },
      {
        label: 'EventListExtensionError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_EXTENSION_ERROR,
      },
      {
        label: 'EventListSourceListError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_SOURCE_LIST_ERROR,
      },
      {
        label: 'EventListDestinationError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_DESTINATION_ERROR,
      },
      {
        label: 'EventListParentIdError',
        value: DocumentExceptionErrorTypeEnum.EVENT_LIST_PARENT_ID_ERROR,
      },
      {
        label: 'AggregationEventOneOfError',
        value: DocumentExceptionErrorTypeEnum.AGGREGATION_EVENT_ONE_OF_ERROR,
      },
      {
        label: 'TransactionEventOneOfError',
        value: DocumentExceptionErrorTypeEnum.TRANSACTION_EVENT_ONE_OF_ERROR,
      },
      {
        label: 'ObjectEventOneOfError',
        value: DocumentExceptionErrorTypeEnum.OBJECT_EVENT_ONE_OF_ERROR,
      },
      {
        label: 'UnknownLocationError',
        value: DocumentExceptionErrorTypeEnum.UNKNOWN_LOCATION_ERROR,
      },
      {
        label: 'UnknownMedError',
        value: DocumentExceptionErrorTypeEnum.UNKNOWN_MED_ERROR,
      },
      {
        label: 'InvalidEventReadPointError',
        value: DocumentExceptionErrorTypeEnum.INVALID_EVENT_READ_POINT_ERROR,
      },
      {
        label: 'UnknownAddEpcError',
        value: DocumentExceptionErrorTypeEnum.UNKNOWN_ADD_EPC_ERROR,
      },
      {
        label: 'UnknownShippingEpcError',
        value: DocumentExceptionErrorTypeEnum.UNKNOWN_SHIPPING_EPC_ERROR,
      },
      {
        label: 'ShippedEpcAreParentOrDefinedError',
        value:
          DocumentExceptionErrorTypeEnum.SHIPPED_EPC_ARE_NOT_PARENT_OR_DEFINED_ERROR,
      },
      {
        label: 'UnknownParentEpcError',
        value: DocumentExceptionErrorTypeEnum.UNKNOWN_PARENT_EPC_ERROR,
      },
      {
        label: 'AllMedsNotShippedError',
        value: DocumentExceptionErrorTypeEnum.ALL_MEDS_NOT_SHIPPED_ERROR,
      },
      {
        label: 'InvalidFileExtensionError',
        value: DocumentExceptionErrorTypeEnum.INVALID_FILE_EXTENSION_ERROR,
      },
      {
        label: 'EmptyFileError',
        value: DocumentExceptionErrorTypeEnum.EMPTY_FILE_ERROR,
      },
      {
        label: 'MedVocabularyMissingNdcError',
        value: DocumentExceptionErrorTypeEnum.MED_VOCABULARY_MISSING_NDC_ERROR,
      },
      {
        label: 'MedVocabularyMalformedNdcError',
        value:
          DocumentExceptionErrorTypeEnum.MED_VOCABULARY_MALFORMED_NDC_ERROR,
      },
      {
        label: 'MultipleDestinationGlns',
        value: DocumentExceptionErrorTypeEnum.MULTIPLE_DESTINATION_GLNS,
      },
      {
        label: 'ReplacementEpcisValidationError',
        value:
          DocumentExceptionErrorTypeEnum.REPLACEMENT_EPCIS_VALIDATION_ERROR,
      },
      {
        label: 'ShippingEventError',
        value: DocumentExceptionErrorTypeEnum.SHIPPING_EVENT_ERROR,
      },
    ],
    []
  );

  const [filters, setFilters] = useState({
    sender: '',
    receiver: '',
    startDate: '',
    endDate: '',
    status: null,
    errorType: null,
  });
  const debouncedFilters = useDebounceValue(filters, 500);

  // Main data store
  const { data: documentExceptions = null, isLoading } =
    queryHooks.documentException.useSearch(debouncedFilters, {
      staleTime: 5 * 60 * 1000,
    });

  const TableRow = useMemo(
    () => styled.tr`
      cursor: pointer;
      &:hover {
        background-color: #e0f2fe;
      }
    `,
    []
  );

  const filterChange = ({ target: { id, value } }) => {
    setFilters(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  const openDetailsPage = id => {
    if (!id) return;
    navigate(`/exceptions/${id}`, false);
  };

  useEffect(() => {
    M.FormSelect.init(document.querySelectorAll('.exceptionsSelect'));
  }, []);

  return (
    <div className="">
      <div className="">
        <div className="row">
          <div className="input-field col s6 m6 l6">
            <input
              id="sender"
              type="text"
              value={filters.sender || ''}
              onChange={filterChange}
            />
            <label htmlFor="sender">Sender</label>
          </div>
          <div className="input-field col s6 m6 l6">
            <input
              id="receiver"
              type="text"
              value={filters.receiver || ''}
              onChange={filterChange}
            />
            <label htmlFor="receiver">Receiver</label>
          </div>
          <div className="input-field col s3 m3 l3">
            <input
              id="startDate"
              type="date"
              value={filters.startDate || ''}
              onChange={filterChange}
            />
            <label htmlFor="startDate">Start</label>
          </div>
          <div className="input-field col s3 m3 l3">
            <input
              id="endDate"
              type="date"
              value={filters.endDate || ''}
              onChange={filterChange}
            />
            <label htmlFor="endDate">End</label>
          </div>
          <div className="input-field col s3 m3 l3">
            <select
              type="select"
              id="status"
              className="exceptionsSelect"
              onChange={filterChange}
              label="Error Status"
              value={filters.status}
              options={errorStatusOptions}
            >
              {errorStatusOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label htmlFor="status">Error Status</label>
          </div>

          <div className="input-field col s3 m3 l3">
            <select
              type="select"
              id="errorType"
              className="exceptionsSelect"
              onChange={filterChange}
              label="Error Type"
              value={filters.errorType}
              options={errorTypeOptions}
            >
              {errorTypeOptions.map(option => (
                <option value={option.value}>{option.label}</option>
              ))}
            </select>
            <label htmlFor="errorType">Error Type</label>
          </div>
        </div>
        {isLoading ? (
          <div
            className="h-64"
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <div className="preloader-wrapper big active">
              <div className="spinner-layer spinner-blue-only">
                <div className="circle-clipper left">
                  <div className="circle"></div>
                </div>
                <div className="gap-patch">
                  <div className="circle"></div>
                </div>
                <div className="circle-clipper right">
                  <div className="circle"></div>
                </div>
              </div>
            </div>{' '}
          </div>
        ) : (
          <div className="card" style={{ padding: '16px' }}>
            <table>
              <thead>
                <tr>
                  {[
                    'Ticket #',
                    'Sender',
                    'Receiver',
                    'Error',
                    'Status',
                    'Identifier',
                    'PO Number',
                    'Date Created',
                    '',
                  ].map(h => (
                    <th>{h}</th>
                  ))}
                </tr>
              </thead>
              {(documentExceptions ?? []).map((ee, index) => {
                return (
                  <tbody>
                    <TableRow
                      key={index}
                      onClick={() => openDetailsPage(ee.id)}
                    >
                      <td>{ee?.ticketNumber}</td>
                      <td>{ee?.sender}</td>
                      <td>{ee?.receiver}</td>
                      <td>{ee?.currentErrorType}</td>
                      <td
                        className={`${ee?.currentErrorStatus?.toLowerCase()}`}
                      >
                        {ee?.currentErrorStatus}
                      </td>
                      <td>{ee?.instanceIdentifier}</td>
                      <td>{ee?.poNumber}</td>
                      <td>{ee?.dateCreated}</td>
                    </TableRow>
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
